import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import DemoViewBanner from '../demo/DemoViewBanner';
import { getActiveSchoolId, isViewingDemoSchool } from '../utils/activeSchool';
import { getApiBaseUrl } from '../apiBase';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/dateFormat';


const INVITE_STATUS_KEYS = {
    pending: 'inviteStatus_pending',
    sent: 'inviteStatus_sent',
    activated_pending_confirmation: 'inviteStatus_activated',
    accepted: 'inviteStatus_accepted',
    declined: 'inviteStatus_declined',
    expired: 'inviteStatus_expired',
};

const INVITE_STATUS_ICONS = {
    pending: '⏳',
    sent: '📨',
    activated_pending_confirmation: '🔗',
    accepted: '✅',
    declined: '❌',
    expired: '⌛',
};

function formatInviteContact(invite) {
    if (invite.recipientPhone) return invite.recipientPhone;
    if (invite.recipientEmail) return invite.recipientEmail;
    return '—';
}

export default function ApplicationsPage() {
    const { t } = useTranslation(['applications', 'common', 'nav', 'messages', 'errors']);
    const STATUS_CONFIG = useMemo(() => ({
        pending: { label: t('applications:statusPending'), color: '#e65100', bg: '#fff3e0' },
        approved: { label: t('applications:statusApproved'), color: '#2e7d32', bg: '#e8f5e9' },
        rejected: { label: t('applications:statusRejected'), color: '#c62828', bg: '#fce4ec' }
    }), [t]);
    const FILTER_TABS = useMemo(() => [
        { key: 'pending', label: t('applications:tabPending') },
        { key: 'approved', label: t('applications:tabApproved') },
        { key: 'rejected', label: t('applications:tabRejected') },
        { key: 'all', label: t('applications:tabAll') },
        { key: 'invites', label: t('applications:invitesTab') },
    ], [t]);
    const [apps, setApps] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const isDemo = isViewingDemoSchool();

    const token = localStorage.getItem('token');
    const api = useMemo(() => axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${token}` }
    }), [token]);

    const loadApps = useCallback(async () => {
        setLoading(true);
        try {
            const schoolId = getActiveSchoolId();
            const params = schoolId ? { schoolId } : {};
            const res = await api.get('/application/incoming', { params });
            setApps(res.data?.items ?? res.data);
        } catch {
            alert(t('applications:loadError'));
        }
        setLoading(false);
    }, [api, t]);

    const loadInvites = useCallback(async () => {
        setLoading(true);
        try {
            const schoolId = getActiveSchoolId();
            if (!schoolId) {
                setInvites([]);
                setLoading(false);
                return;
            }
            const res = await api.get('/invite/school-invites', { params: { schoolId } });
            setInvites(res.data?.items ?? []);
        } catch {
            alert(t('applications:loadError'));
        }
        setLoading(false);
    }, [api, t]);

    useEffect(() => {
        if (filter === 'invites') loadInvites();
        else loadApps();
    }, [filter, loadApps, loadInvites]);

    async function handleApprove(id) {
        if (isDemo) return alert(t('applications:demoApprove'));
        try {
            const res = await api.post(`/application/${id}/approve`, { response: t('applications:statusApproved') });
            if (res.data?.studentCreated) {
                alert(t('applications:approvedMsg'));
            }
            loadApps();
        } catch (err) {
            alert(err.response?.data?.message || t('common:error'));
        }
    }

    async function handleEnsureStudent(id) {
        if (isDemo) return alert(t('applications:demoAction'));
        try {
            const res = await api.post(`/application/${id}/ensure-student`);
            alert(res.data?.message || t('common:success'));
            loadApps();
        } catch (err) {
            alert(err.response?.data?.message || t('common:error'));
        }
    }

    async function handleReject(id) {
        if (isDemo) return alert(t('applications:demoReject'));
        const response = window.prompt(t('applications:rejectPrompt'));
        if (response === null) return;
        try {
            await api.post(`/application/${id}/reject`, { response });
            loadApps();
        } catch (err) {
            alert(err.response?.data?.message || t('common:error'));
        }
    }

    async function handleResendInvite(inviteToken) {
        if (isDemo) return alert(t('applications:demoAction'));
        try {
            await api.post(`/invite/${inviteToken}/resend`, {});
            alert(t('common:success'));
            loadInvites();
        } catch (err) {
            alert(err.response?.data?.message || err.response?.data?.error || t('common:error'));
        }
    }

    async function handleCancelInvite(id) {
        if (isDemo) return alert(t('applications:demoAction'));
        if (!window.confirm(t('applications:cancelInvite') + '?')) return;
        try {
            const schoolId = getActiveSchoolId();
            await api.delete(`/invite/${id}`, { params: { schoolId } });
            loadInvites();
        } catch (err) {
            alert(err.response?.data?.message || t('common:error'));
        }
    }

    const filtered = apps.filter(a => filter === 'all' || a.status === filter);

    const inviteStatusLabel = (status) => {
        const key = INVITE_STATUS_KEYS[status];
        const icon = INVITE_STATUS_ICONS[status] ?? '•';
        return key ? `${icon} ${t(`applications:${key}`)}` : status;
    };

    const canResendInvite = (status) =>
        status === 'pending' || status === 'sent' || status === 'expired';

    const canCancelInvite = (status) =>
        status === 'pending' || status === 'sent';

    if (loading) return (
        <OwnerPageLayout title={t('applications:title')} maxWidth={960}>
            <div className="sp-loading-page">{t('common:loading')}</div>
        </OwnerPageLayout>
    );

    const pendingCount = apps.filter(a => a.status === 'pending').length;

    return (
        <OwnerPageLayout
            title={t('applications:titleFull')}
            subtitle={filter === 'invites'
                ? t('applications:invitesSubtitle', { count: invites.length })
                : t('applications:subtitle', { count: pendingCount })}
            maxWidth={960}
            beforeMain={<DemoViewBanner />}
        >
            <div className="sp-tabs" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                {FILTER_TABS.map(f => (
                    <button
                        key={f.key}
                        type="button"
                        className={`sp-tab${filter === f.key ? ' sp-tab--active' : ''}`}
                        onClick={() => setFilter(f.key)}
                    >
                        {f.label}
                        {f.key === 'pending' && (
                            <span style={{ marginLeft: '6px' }}>({pendingCount})</span>
                        )}
                        {f.key === 'invites' && (
                            <span style={{ marginLeft: '6px' }}>({invites.length})</span>
                        )}
                        {f.key !== 'all' && f.key !== 'pending' && f.key !== 'invites' && (
                            <span style={{ marginLeft: '6px' }}>
                                ({apps.filter(a => a.status === f.key).length})
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {filter === 'invites' ? (
                invites.length === 0 ? (
                    <div className="sp-panel" style={{ textAlign: 'center', padding: '48px', color: 'var(--sp-gray-400)' }}>
                        <div style={{ fontSize: '40px', marginBottom: '12px' }}>📨</div>
                        {t('applications:invitesEmpty')}
                    </div>
                ) : (
                    <div className="sp-panel" style={{ overflowX: 'auto', padding: 0 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                                <tr style={{ background: 'var(--sp-gray-50)', textAlign: 'left' }}>
                                    <th style={{ padding: '12px 14px' }}>{t('applications:inviteColName')}</th>
                                    <th style={{ padding: '12px 14px' }}>{t('applications:inviteColContact')}</th>
                                    <th style={{ padding: '12px 14px' }}>{t('applications:inviteColRole')}</th>
                                    <th style={{ padding: '12px 14px' }}>{t('applications:inviteColStatus')}</th>
                                    <th style={{ padding: '12px 14px' }}>{t('applications:inviteColSent')}</th>
                                    <th style={{ padding: '12px 14px' }}>{t('applications:inviteColActions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invites.map(invite => (
                                    <tr key={invite.id} style={{ borderTop: '1px solid var(--sp-gray-100)' }}>
                                        <td style={{ padding: '12px 14px', fontWeight: 600 }}>
                                            {invite.recipientName || '—'}
                                        </td>
                                        <td style={{ padding: '12px 14px', color: '#555' }}>
                                            {formatInviteContact(invite)}
                                        </td>
                                        <td style={{ padding: '12px 14px' }}>
                                            {invite.expectedRole === 'Coach'
                                                ? t('applications:roleCoach')
                                                : t('applications:roleAthlete')}
                                        </td>
                                        <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                                            {inviteStatusLabel(invite.status)}
                                        </td>
                                        <td style={{ padding: '12px 14px', color: '#888', whiteSpace: 'nowrap' }}>
                                            {formatDate(invite.createdAt)}
                                        </td>
                                        <td style={{ padding: '12px 14px' }}>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                {canResendInvite(invite.status) && !isDemo && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleResendInvite(invite.token)}
                                                        style={{
                                                            padding: '6px 10px', fontSize: '12px', fontWeight: 600,
                                                            background: '#e3f2fd', color: '#1565c0', border: 'none',
                                                            borderRadius: '8px', cursor: 'pointer'
                                                        }}
                                                    >
                                                        📨 {t('applications:resendInvite')}
                                                    </button>
                                                )}
                                                {canCancelInvite(invite.status) && !isDemo && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCancelInvite(invite.id)}
                                                        style={{
                                                            padding: '6px 10px', fontSize: '12px', fontWeight: 600,
                                                            background: '#fce4ec', color: '#c62828', border: 'none',
                                                            borderRadius: '8px', cursor: 'pointer'
                                                        }}
                                                    >
                                                        ✕ {t('applications:cancelInvite')}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : filtered.length === 0 ? (
                <div className="sp-panel" style={{ textAlign: 'center', padding: '48px', color: 'var(--sp-gray-400)' }}>
                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
                    {t('applications:empty')}
                </div>
            ) : (
                <div style={{ display: 'grid', gap: '10px' }}>
                    {filtered.map(app => {
                        const conf = STATUS_CONFIG[app.status];
                        return (
                            <div key={app.id} style={{
                                background: 'white', borderRadius: '14px',
                                padding: '18px 20px', border: '1px solid var(--sp-gray-100)'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                                            <div style={{
                                                width: '38px', height: '38px', borderRadius: '50%',
                                                background: '#1A1A1A', color: 'white',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontWeight: 'bold', fontSize: '16px'
                                            }}>
                                                {app.username?.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: '700', fontSize: '15px' }}>{app.username}</div>
                                                <div style={{ fontSize: '13px', color: '#888' }}>{app.email}</div>
                                            </div>
                                            <span style={{
                                                marginLeft: '8px',
                                                background: app.role === 'coach' ? '#e3f2fd' : '#f3e5f5',
                                                color: app.role === 'coach' ? '#EB3333' : '#7b1fa2',
                                                padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600'
                                            }}>
                                                {app.role === 'coach' ? t('applications:roleCoach') : t('applications:roleAthlete')}
                                            </span>
                                        </div>
                                        {app.message && (
                                            <div style={{
                                                background: 'var(--sp-gray-50)', borderRadius: '8px',
                                                padding: '8px 12px', fontSize: '13px', color: '#555',
                                                marginTop: '8px'
                                            }}>
                                                💬 {app.message}
                                            </div>
                                        )}
                                        <div style={{ fontSize: '12px', color: '#aaa', marginTop: '8px' }}>
                                            {formatDate(app.createdAt)}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '16px' }}>
                                        <span style={{
                                            background: conf?.bg ?? '#f5f5f5', color: conf?.color ?? '#666',
                                            padding: '4px 12px', borderRadius: '10px',
                                            fontSize: '12px', fontWeight: '700', textAlign: 'center'
                                        }}>
                                            {conf?.label ?? app.status}
                                        </span>
                                        {app.status === 'pending' && !isDemo && (
                                            <>
                                                <button onClick={() => handleApprove(app.id)} style={{
                                                    padding: '8px 16px', background: '#2ecc71', color: 'white',
                                                    border: 'none', borderRadius: '8px', cursor: 'pointer',
                                                    fontSize: '13px', fontWeight: '600'
                                                }}>
                                                    {t('applications:approve')}
                                                </button>
                                                <button onClick={() => handleReject(app.id)} style={{
                                                    padding: '8px 16px', background: '#fce4ec', color: '#c62828',
                                                    border: '1px solid #ffcdd2', borderRadius: '8px',
                                                    cursor: 'pointer', fontSize: '13px', fontWeight: '600'
                                                }}>
                                                    {t('applications:reject')}
                                                </button>
                                            </>
                                        )}
                                        {app.status === 'approved' &&
                                            app.role === 'student' &&
                                            !app.hasStudentProfile &&
                                            !isDemo && (
                                            <button onClick={() => handleEnsureStudent(app.id)} style={{
                                                padding: '8px 16px', background: '#EB3333', color: 'white',
                                                border: 'none', borderRadius: '8px', cursor: 'pointer',
                                                fontSize: '13px', fontWeight: '600'
                                            }}>
                                                {t('applications:createProfile')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </OwnerPageLayout>
    );
}
